import { useState, useEffect, useMemo } from "react";
import { 
  Calendar, Users, LogOut, Clock, BarChart3, CalendarDays, Settings, 
  Package, LayoutDashboard, PanelLeftClose, PanelLeft, Activity,
  Share2, MessageSquare, Globe, Dumbbell, Apple, ClipboardList, TrendingUp, UserCircle
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const LANG_OPTIONS = [
  { key: "de", label: "Deutsch", flag: "DE" },
  { key: "en", label: "English", flag: "EN" },
  { key: "pt", label: "Português", flag: "PT" },
];

export const Layout = ({ children, onLogout }) => {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { lang, setLang, t } = useLanguage();
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const [notifCounts, setNotifCounts] = useState({ low_stock: 0, total: 0 });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setNotifCounts(res.data.counts || {}))
      .catch(() => {});
  }, [location.pathname]);

  const navItems = [
    { path: "/manager", labelKey: "nav_dashboard", icon: LayoutDashboard },
    { path: "/", labelKey: "nav_calendar", icon: Calendar },
    { path: "/weekly", labelKey: "nav_weekly", icon: CalendarDays },
    { path: "/bioimpedance", labelKey: "nav_bioimpedance", icon: Activity },
    { path: "/social-media", labelKey: "nav_social_media", icon: Share2 },
    { path: "/crm", labelKey: "nav_crm", icon: Users },
    { path: "/ai-chat", labelKey: "nav_ai_chat", icon: MessageSquare },
    { path: "/inventory", labelKey: "nav_inventory", icon: Package },
    { path: "/workers", labelKey: "nav_workers", icon: Users },
    { path: "/shift-types", labelKey: "nav_shift_types", icon: Clock },
    { path: "/training-plans", labelKey: "nav_training_plans", icon: Dumbbell },
    { path: "/nutrition", labelKey: "nav_nutrition", icon: Apple },
    { path: "/anamnese", labelKey: "nav_anamnese", icon: ClipboardList },
    { path: "/statistics", labelKey: "nav_statistics", icon: BarChart3 },
    { path: "/integrations", labelKey: "nav_integrations", icon: Settings },
    { path: "/revenue", labelKey: "nav_revenue", icon: TrendingUp, adminOnly: true },
  ];

  const isAdminUser = localStorage.getItem('is_admin') === 'true';
  const visibleNavItems = useMemo(() => navItems.filter(item => !item.adminOnly || isAdminUser), [isAdminUser]);

  const currentLang = LANG_OPTIONS.find(l => l.key === lang) || LANG_OPTIONS[0];

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-slate-50 overflow-hidden">
        <aside 
          className={`${isCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-slate-200 flex flex-col transition-all duration-300 ease-in-out relative`} 
          data-testid="sidebar"
        >
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute -right-3 top-6 z-10 bg-white border border-slate-200 rounded-full p-1.5 shadow-sm hover:bg-slate-50 transition-colors"
            data-testid="sidebar-toggle"
            title={isCollapsed ? t("expand_menu") : t("collapse_menu")}
          >
            {isCollapsed ? <PanelLeft className="w-4 h-4 text-slate-600" /> : <PanelLeftClose className="w-4 h-4 text-slate-600" />}
          </button>

          <div className={`p-4 border-b border-slate-200 ${isCollapsed ? 'px-3' : 'p-6'}`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              {!isCollapsed && (
                <div className="overflow-hidden">
                  <h1 className="text-lg font-bold text-slate-900 whitespace-nowrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Athlo Nexus
                  </h1>
                  <p className="text-xs text-slate-600">Studio Manager</p>
                </div>
              )}
            </div>
          </div>

          <nav className={`${isCollapsed ? 'p-2' : 'p-4'} space-y-1 flex-1 overflow-y-auto`}>
            {visibleNavItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const label = t(item.labelKey);
              
              const linkContent = (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-2.5 rounded-md transition-colors ${
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={`nav-${item.labelKey}`}
                >
                  <div className="relative flex-shrink-0">
                    <Icon className="w-5 h-5" />
                    {item.path === "/inventory" && notifCounts.low_stock > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="inventory-badge">
                        {notifCounts.low_stock}
                      </span>
                    )}
                    {item.path === "/manager" && notifCounts.total > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-amber-500 rounded-full" data-testid="manager-badge" />
                    )}
                  </div>
                  {!isCollapsed && (
                    <span className="font-medium whitespace-nowrap overflow-hidden">{label}</span>
                  )}
                </Link>
              );

              if (isCollapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-slate-900 text-white">{label}</TooltipContent>
                  </Tooltip>
                );
              }
              return linkContent;
            })}
          </nav>

          <div className={`${isCollapsed ? 'w-16' : 'w-64'} p-3 border-t border-slate-200 bg-white transition-all duration-300`}>
            {!isCollapsed ? (
              <>
                <div className="mb-2 cursor-pointer hover:bg-slate-50 rounded-lg p-1.5 -mx-1.5 transition-colors" onClick={() => window.location.href = '/profile'} data-testid="sidebar-profile-link">
                  <div className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="overflow-hidden">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Profil</p>
                      <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
                      <p className="text-xs text-slate-600 truncate">{user.email}</p>
                    </div>
                  </div>
                </div>
                
                {/* Language Selector */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-start mb-2 text-slate-600 hover:text-slate-900" data-testid="language-selector">
                      <Globe className="w-4 h-4 mr-2" />
                      {currentLang.flag} {currentLang.label}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {LANG_OPTIONS.map(opt => (
                      <DropdownMenuItem key={opt.key} onClick={() => setLang(opt.key)} data-testid={`lang-${opt.key}`}>
                        <span className="font-medium mr-2">{opt.flag}</span> {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button onClick={onLogout} variant="outline" className="w-full justify-start" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />{t("logout")}
                </Button>
              </>
            ) : (
              <div className="space-y-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-full" data-testid="language-selector">
                      <Globe className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right">
                    {LANG_OPTIONS.map(opt => (
                      <DropdownMenuItem key={opt.key} onClick={() => setLang(opt.key)} data-testid={`lang-${opt.key}`}>
                        <span className="font-medium mr-2">{opt.flag}</span> {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={onLogout} variant="outline" size="icon" className="w-full" data-testid="logout-button">
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-slate-900 text-white">
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs opacity-80">{user.email}</p>
                    <p className="text-xs mt-1">{t("logout")}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </aside>

        <main className="flex-1 overflow-hidden flex flex-col">{children}</main>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
